/**
 * Savings goals — progress, and an honest answer to "when does this land?".
 *
 * We store the balance of a goal, not each individual deposit, so there is no
 * contribution history to extrapolate a finish date from. Rather than invent a
 * pace, the panel shows what the goal *requires* per month to hit its target
 * date (real math from real numbers) and says plainly that a pace projection
 * needs contribution history it does not have.
 */
import { useCallback, useMemo, useState } from 'react';
import type { SavingsGoal } from '../../shared/types';
import { api, money, parseMoney } from '../../lib/api';
import {
  Button,
  Card,
  Chip,
  Disclaimer,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PanelHeading,
  ProgressBar,
  TextInput,
  errorText,
  inputClass,
  tokenColor,
  useResource,
} from './ui';

/**
 * The goal palette is the house palette — the accent tokens in src/styles.css.
 * We name the tokens here and resolve them at render, so the hexes exist in
 * exactly one place. If a token ever goes missing the swatch drops out and the
 * goal is saved with no color, letting the D1 column default decide; the app
 * never carries its own copy of the brand.
 */
const SWATCH_TOKENS = [
  { name: 'Sage', token: '--color-sage' },
  { name: 'Clay', token: '--color-clay' },
  { name: 'Honey', token: '--color-honey' },
  { name: 'Dusk', token: '--color-dusk' },
];

function readSwatches(): { name: string; value: string }[] {
  return SWATCH_TOKENS.map((s) => ({ name: s.name, value: tokenColor(s.token) })).filter(
    (s) => s.value !== ''
  );
}

const DAYS_PER_MONTH = 30.44;

function daysUntil(date: string): number {
  const target = Date.parse(`${date}T00:00:00Z`);
  const today = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  return Math.round((target - today) / 86_400_000);
}

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** What this goal asks of you — derived only from numbers we actually have. */
function requirement(goal: SavingsGoal): { tone: 'ok' | 'near' | 'over' | 'neutral'; text: string } {
  const remaining = goal.target - goal.current;

  if (remaining <= 0) {
    return {
      tone: 'ok',
      text: `Fully funded${remaining < 0 ? ` — ${money(-remaining)} past the target` : ''}. Move it somewhere it earns, or set a new target.`,
    };
  }

  if (!goal.target_date) {
    return {
      tone: 'neutral',
      text: `${money(remaining)} to go. Add a target date and we will show the monthly amount it takes to get there.`,
    };
  }

  const days = daysUntil(goal.target_date);

  if (days <= 0) {
    return {
      tone: 'over',
      text: `Target date of ${formatDate(goal.target_date)} has passed and it is ${money(remaining)} short. Move the date or lower the target.`,
    };
  }

  const months = Math.max(days / DAYS_PER_MONTH, 0.25);
  const perMonth = Math.round(remaining / months);
  const perWeek = Math.round(remaining / Math.max(days / 7, 1));

  return {
    tone: days < 45 ? 'near' : 'neutral',
    text: `${money(remaining)} to go in ${days} ${days === 1 ? 'day' : 'days'}. To land by ${formatDate(
      goal.target_date
    )} that is about ${money(perMonth)} a month, or ${money(perWeek)} a week.`,
  };
}

function GoalCard({
  goal,
  busy,
  onPatch,
  onRemove,
}: {
  goal: SavingsGoal;
  busy: boolean;
  onPatch: (id: number, patch: Partial<SavingsGoal>) => Promise<void>;
  onRemove: (goal: SavingsGoal) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [contribution, setContribution] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const swatches = useMemo(readSwatches, []);

  const percent = goal.target > 0 ? (goal.current / goal.target) * 100 : 0;
  const req = requirement(goal);

  async function addContribution(e: React.FormEvent) {
    e.preventDefault();
    const cents = parseMoney(contribution);
    if (cents === null || cents === 0) {
      setLocalError('Enter an amount, like 250 or 250.00.');
      return;
    }
    setLocalError(null);
    await onPatch(goal.id, { current: Math.max(0, goal.current + cents) });
    setContribution('');
  }

  async function saveEdit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const target = parseMoney(String(fd.get('target') ?? ''));
    const current = parseMoney(String(fd.get('current') ?? ''));
    if (target === null || target <= 0) {
      setLocalError('Target must be an amount above zero.');
      return;
    }
    if (current === null || current < 0) {
      setLocalError('Saved so far must be zero or more.');
      return;
    }
    setLocalError(null);
    await onPatch(goal.id, {
      name: String(fd.get('name') ?? '').trim(),
      target,
      current,
      target_date: String(fd.get('target_date') ?? '') || null,
      color: String(fd.get('color') ?? goal.color),
    });
    setEditing(false);
  }

  return (
    <Card as="li">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: goal.color }}
          />
          <h3 className="font-display text-lg font-semibold text-ink">{goal.name}</h3>
          {goal.current >= goal.target && <Chip tone="ok">Funded</Chip>}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setEditing((v) => !v)} aria-expanded={editing}>
            {editing ? 'Close' : 'Edit'}
          </Button>
          <Button variant="danger" onClick={() => onRemove(goal)} disabled={busy}>
            Remove
          </Button>
        </div>
      </div>

      <p className="mt-2 text-sm text-ink-soft">
        <span className="font-display text-2xl font-semibold text-ink">{money(goal.current)}</span> of{' '}
        {money(goal.target)} · {Math.round(percent)}%
      </p>

      <div className="mt-2">
        <ProgressBar
          percent={percent}
          color={goal.color}
          label={`${goal.name}: ${Math.round(percent)} percent funded`}
        />
      </div>

      <p
        className={`mt-2 text-sm leading-relaxed ${
          req.tone === 'over' ? 'text-over' : req.tone === 'ok' ? 'text-ok' : 'text-ink-soft'
        }`}
      >
        {req.text}
      </p>

      {!editing && (
        <form onSubmit={addContribution} className="mt-3 flex flex-wrap items-end gap-2">
          <Field label={`Add to ${goal.name}`} className="max-w-40 flex-1">
            <TextInput
              value={contribution}
              onChange={(e) => setContribution(e.target.value)}
              inputMode="decimal"
              placeholder="250.00"
              aria-describedby={`goal-${goal.id}-hint`}
            />
          </Field>
          <Button type="submit" variant="primary" disabled={busy}>
            Add
          </Button>
          <span id={`goal-${goal.id}-hint`} className="sr-only">
            Adds this amount to the saved balance for {goal.name}.
          </span>
        </form>
      )}

      {editing && (
        <form onSubmit={saveEdit} className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-2">
          <Field label="Name">
            <TextInput name="name" defaultValue={goal.name} required />
          </Field>
          <Field label="Target amount">
            <TextInput
              name="target"
              defaultValue={(goal.target / 100).toFixed(2)}
              inputMode="decimal"
              required
            />
          </Field>
          <Field label="Saved so far">
            <TextInput
              name="current"
              defaultValue={(goal.current / 100).toFixed(2)}
              inputMode="decimal"
              required
            />
          </Field>
          <Field label="Target date" hint="Optional.">
            <input
              type="date"
              name="target_date"
              defaultValue={goal.target_date ?? ''}
              className={inputClass}
            />
          </Field>
          <Field label="Color" className="sm:col-span-2">
            <select name="color" defaultValue={goal.color} className={inputClass}>
              {swatches.every((s) => s.value !== goal.color) && (
                <option value={goal.color}>Current ({goal.color})</option>
              )}
              {swatches.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" variant="primary" disabled={busy}>
              Save changes
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {localError && (
        <p role="alert" className="mt-2 text-sm text-clay">
          {localError}
        </p>
      )}
    </Card>
  );
}

export default function GoalsPanel() {
  const load = useCallback(() => api.get<SavingsGoal[]>('/insights/goals'), []);
  const { data, loading, error, reload, setData } = useResource(load, []);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const swatches = useMemo(readSwatches, []);

  const goals = data ?? [];

  async function patch(id: number, body: Partial<SavingsGoal>) {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.put<SavingsGoal>(`/insights/goals/${id}`, body);
      setData(goals.map((g) => (g.id === id ? updated : g)));
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(goal: SavingsGoal) {
    if (!window.confirm(`Remove "${goal.name}"? The goal is archived, not deleted.`)) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.del<SavingsGoal>(`/insights/goals/${goal.id}`);
      setData(goals.filter((g) => g.id !== goal.id));
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function add(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const target = parseMoney(String(fd.get('target') ?? ''));
    if (target === null || target <= 0) {
      setActionError('Give the goal a target above zero, like 5000.');
      return;
    }

    // A typo in the starting balance used to be silently coerced to zero, which
    // quietly threw away the money you had already saved. Validate it the same
    // way the edit form does.
    const currentRaw = String(fd.get('current') ?? '').trim();
    const current = currentRaw === '' ? 0 : parseMoney(currentRaw);
    if (current === null || current < 0) {
      setActionError('Saved so far must be an amount of zero or more, like 0 or 250.00.');
      return;
    }

    // No color chosen (or the token is missing) → let D1's column default decide.
    const color = String(fd.get('color') ?? '').trim();

    setBusy(true);
    setActionError(null);
    try {
      const created = await api.post<SavingsGoal>('/insights/goals', {
        name: String(fd.get('name') ?? '').trim(),
        target,
        current,
        target_date: String(fd.get('target_date') ?? '') || null,
        color: color || undefined,
      });
      setData([...goals, created]);
      setAdding(false);
      form.reset();
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const addForm = (
    <Card className="mb-4">
      <form onSubmit={add} className="grid gap-3 sm:grid-cols-2">
        <Field label="What are you saving for?">
          <TextInput name="name" required placeholder="Emergency fund" />
        </Field>
        <Field label="Target amount" hint="Dollars, e.g. 10000">
          <TextInput name="target" required inputMode="decimal" placeholder="10000" />
        </Field>
        <Field label="Saved so far" hint="Leave at 0 if you are starting fresh.">
          <TextInput name="current" inputMode="decimal" defaultValue="0" />
        </Field>
        <Field label="Target date" hint="Optional — unlocks the monthly amount it takes.">
          <input type="date" name="target_date" className={inputClass} />
        </Field>
        <Field label="Color" className="sm:col-span-2">
          <select name="color" defaultValue={swatches[0]?.value ?? ''} className={inputClass}>
            {swatches.map((s) => (
              <option key={s.value} value={s.value}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={busy}>
            Create goal
          </Button>
          <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );

  const totalSaved = goals.reduce((sum, g) => sum + g.current, 0);
  const totalTarget = goals.reduce((sum, g) => sum + g.target, 0);

  return (
    <div>
      <PanelHeading
        title="Savings goals"
        sub="What you are putting money aside for, and what each one still asks of you."
        action={
          goals.length > 0 ? (
            <Button variant="primary" onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
              New goal
            </Button>
          ) : undefined
        }
      />

      {adding && addForm}

      {actionError && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-clay/30 bg-clay-soft px-3 py-2 text-sm text-clay"
        >
          {actionError}
        </p>
      )}

      {loading && <Loading label="Loading savings goals" rows={2} />}

      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && goals.length === 0 && !adding && (
        <EmptyState
          title="No goals yet"
          action={
            <Button variant="primary" onClick={() => setAdding(true)}>
              Add your first goal
            </Button>
          }
        >
          A goal is a name, a number, and (optionally) a date. Start with the one you talk about most —
          an emergency fund, the next car, a trip — and this will tell you what it takes each month to
          land it.
        </EmptyState>
      )}

      {!loading && !error && goals.length > 0 && (
        <div className="space-y-4">
          <Card className="bg-surface-sunk">
            <p className="text-sm text-ink-soft">Across {goals.length} {goals.length === 1 ? 'goal' : 'goals'}</p>
            <p className="font-display text-2xl font-semibold text-ink">
              {money(totalSaved)} <span className="text-base font-normal text-ink-soft">of {money(totalTarget)}</span>
            </p>
            <div className="mt-2">
              <ProgressBar
                percent={totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0}
                color="var(--color-sage)"
                label="All goals combined"
              />
            </div>
          </Card>

          <ul className="space-y-3">
            {goals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} busy={busy} onPatch={patch} onRemove={remove} />
            ))}
          </ul>

          <Disclaimer>
            No finish-date projection here on purpose: the app stores each goal's balance, not the
            individual deposits, so there is no contribution pace to extrapolate from — and a made-up
            date is worse than none. The monthly figures above are plain arithmetic on the numbers you
            entered. Where to actually put the money is a question for a licensed professional.
          </Disclaimer>
        </div>
      )}
    </div>
  );
}
